import type { Artifact } from "@/api/models"

export interface ArtifactLineage {
  latest: Artifact
  versions: Artifact[]
}

export function getArtifactTitle(artifact: Artifact): string {
  return artifact.title.trim().length > 0 ? artifact.title : artifact.type
}

/**
 * Group artifacts into chip groups by their lineage root id, falling back
 * to the artifact's own id when no parent is known. Two same-type same-title
 * but lineage-independent artifacts must NOT collide (#R6).
 *
 * Pass `rootId` to anchor the group on the lineage root (preferred —
 * `WorkspaceLayout` walks the parent chain via `getVersionChain` and passes
 * the root). Omit it for callers that only have the bare artifact and want
 * a stable per-artifact key (uses `parent_id ?? id` as a best effort).
 *
 * Type is included so a child of one root never accidentally co-groups with
 * a same-id key from a different artifact type, and to keep the prefix
 * useful as a coarse selector.
 */
export function getLineageGroupKey(artifact: Artifact, rootId?: string): string {
  const root = rootId ?? artifact.parent_id ?? artifact.id
  return `${artifact.type}:${root}`
}

/**
 * Display label for a lineage group. The previous group key (`type:title`)
 * was used both for grouping AND display; now grouping uses the lineage
 * root id (#R6), but the chip strip still wants a human-readable label.
 */
export function getLineageDisplayLabel(artifact: Artifact): string {
  return `${artifact.type}:${getArtifactTitle(artifact).toLocaleLowerCase()}`
}
